import { useEffect, useId, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  Field,
  RadioOption,
  RadioOptionGroup,
  ScoreScale,
  Spinner,
  Textarea,
  Wordmark,
} from '@wundo/ui';
import { ApiError } from '@wundo/client/api.ts';
import type { PostponedConversationAnswer } from '@wundo/shared/closingSurvey.ts';
import { m } from '@/paraglide/messages.js';
import { applyUserLocale } from '@/lib/locale.ts';
import {
  fetchClosingSurvey,
  saveClosingSurveyAnswers,
  type ClosingSurveyAnswers,
  type ClosingSurveyResponse,
} from '@/lib/closingSurvey.ts';

/*
 * The short form a pilot's closing mail links to. Outside `_app`, like the
 * unsubscribe page: whoever opens it has an inbox and often no account any
 * more. The signed token in the URL is the whole of who they are.
 */
export const Route = createFileRoute('/survey')({
  component: SurveyRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
});

function SurveyRoute() {
  const { token } = Route.useSearch();

  return <SurveyPage token={token} />;
}

export function SurveyPage({ token }: { token: string | undefined }) {
  const survey = useQuery({
    queryKey: ['closingSurvey', token],
    queryFn: () => fetchClosingSurvey(token ?? ''),
    enabled: Boolean(token),
    retry: false,
  });

  /* The form reads in the language the mail was written in, whatever the browser says. */
  useEffect(() => {
    applyUserLocale(survey.data?.language);
  }, [survey.data?.language]);

  const invalid =
    !token || (survey.error instanceof ApiError && survey.error.status === 404);

  return (
    <main className='mx-auto flex min-h-dvh max-w-150 flex-col gap-6 py-8 mobile:px-6 mobile:py-12'>
      {/* The cards bleed on a phone; the inset keeps the logo in line with their text. */}
      <div className='max-mobile:px-5.5'>
        <Wordmark registered className='h-8' />
      </div>

      <SurveyBody token={token} invalid={invalid} survey={survey} />
    </main>
  );
}

interface SurveyBodyProps {
  token: string | undefined;
  invalid: boolean;
  survey: { isError: boolean; data: ClosingSurveyResponse | undefined };
}

function SurveyBody({ token, invalid, survey }: SurveyBodyProps) {
  if (invalid) {
    return (
      <Card bleed='phone'>
        <h1 className='text-xl font-semibold tracking-tight'>
          {m.closing_survey_invalid_title()}
        </h1>
        <p className='mt-3 text-base text-ink-muted'>{m.closing_survey_invalid_body()}</p>
      </Card>
    );
  }
  if (survey.isError) {
    return (
      <Card bleed='phone'>
        <p role='alert' className='text-base text-danger'>
          {m.closing_survey_load_error()}
        </p>
      </Card>
    );
  }
  if (token && survey.data) return <SurveyForm token={token} survey={survey.data} />;

  return (
    <div className='flex justify-center py-12'>
      <Spinner />
    </div>
  );
}

function isComplete(answers: ClosingSurveyAnswers): boolean {
  return (
    answers.nps_score !== null &&
    answers.programme_application_score !== null &&
    answers.postponed_conversation !== null
  );
}

interface SurveyFormProps {
  token: string;
  survey: ClosingSurveyResponse;
}

/*
 * Every answer is saved the moment it is given, the reason when the field is
 * left: someone who stops after the first question has still told us the one
 * number that matters most. The button at the end only says thank you.
 */
function SurveyForm({ token, survey }: SurveyFormProps) {
  const [answers, setAnswers] = useState(survey.answers);
  const [reason, setReason] = useState(survey.answers.nps_reason ?? '');
  const [done, setDone] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [sending, setSending] = useState(false);
  const [alreadyComplete] = useState(() => isComplete(survey.answers));

  /* One save at a time, in the order given, so a slow one never lands after a later answer. */
  const queue = useRef<Promise<boolean>>(Promise.resolve(true));
  const savedReason = useRef(survey.answers.nps_reason ?? '');

  /** Answers whether it was saved; a failure says so under the form. */
  function save(patch: Partial<ClosingSurveyAnswers>): Promise<boolean> {
    queue.current = queue.current.then(() =>
      saveClosingSurveyAnswers(token, patch).then(
        () => {
          setSaveError('');
          return true;
        },
        () => {
          setSaveError(m.closing_survey_save_error());
          return false;
        },
      ),
    );
    return queue.current;
  }

  function answer(patch: Partial<ClosingSurveyAnswers>) {
    setAnswers((current) => ({ ...current, ...patch }));
    void save(patch);
  }

  function saveReason(): Promise<boolean> {
    const trimmed = reason.trim();
    if (trimmed === savedReason.current) return queue.current;

    savedReason.current = trimmed;
    return save({ nps_reason: trimmed || null });
  }

  /*
   * Every answer is sent again, so one that failed earlier gets another chance
   * before the reader is thanked for it.
   */
  async function send() {
    setSending(true);
    savedReason.current = reason.trim();
    const saved = await save({ ...answers, nps_reason: reason.trim() || null });
    setSending(false);
    setDone(saved);
  }

  if (done) {
    return (
      <Card tone='accent' bleed='phone'>
        <h1 className='text-xl font-semibold tracking-tight'>
          {m.closing_survey_done_title()}
        </h1>
        <p className='mt-3 text-base text-ink-muted'>{m.closing_survey_done_body()}</p>
        <Button
          type='button'
          variant='link'
          size='sm'
          className='mt-6'
          onClick={() => setDone(false)}
        >
          {m.closing_survey_edit()}
        </Button>
      </Card>
    );
  }

  return (
    <>
      {alreadyComplete && (
        <Card tone='accent' bleed='phone'>
          <h2 className='text-base font-semibold'>{m.closing_survey_already_title()}</h2>
          <p className='mt-1 text-base text-ink-muted'>
            {m.closing_survey_already_body()}
          </p>
        </Card>
      )}

      <Card bleed='phone'>
        <h1 className='text-xl font-semibold tracking-tight'>
          {m.closing_survey_title()}
        </h1>
        <p className='mt-3 text-base text-ink-muted'>{m.closing_survey_intro()}</p>

        <SurveyQuestions
          answers={answers}
          reason={reason}
          programme={survey.programme_name ?? m.closing_survey_programme_fallback()}
          onAnswer={answer}
          onReasonChange={setReason}
          onReasonBlur={() => void saveReason()}
        />

        {saveError && (
          <p role='alert' className='mt-6 text-sm font-medium text-danger'>
            {saveError}
          </p>
        )}

        <Button
          type='button'
          variant='accent'
          shape='block'
          size='lg'
          className='mt-8'
          fullWidth
          loading={sending}
          onClick={() => void send()}
        >
          {m.closing_survey_submit()}
        </Button>
      </Card>
    </>
  );
}

interface SurveyQuestionsProps {
  answers: ClosingSurveyAnswers;
  reason: string;
  programme: string;
  onAnswer: (patch: Partial<ClosingSurveyAnswers>) => void;
  onReasonChange: (reason: string) => void;
  onReasonBlur: () => void;
}

function SurveyQuestions({
  answers,
  reason,
  programme,
  onAnswer,
  onReasonChange,
  onReasonBlur,
}: SurveyQuestionsProps) {
  const npsId = useId();
  const programmeId = useId();
  const postponedId = useId();

  return (
    <>
      <section className='mt-8 flex flex-col gap-4'>
        <h2 id={npsId} className='text-base font-semibold'>
          1. {m.closing_survey_nps_question()}
        </h2>
        <ScoreScale
          aria-labelledby={npsId}
          min={0}
          max={10}
          value={answers.nps_score}
          onValueChange={(score) => onAnswer({ nps_score: score })}
          lowLabel={m.closing_survey_nps_low()}
          highLabel={m.closing_survey_nps_high()}
        />
        <Field
          label={m.closing_survey_reason_label()}
          description={m.closing_survey_reason_optional()}
        >
          {(control) => (
            <Textarea
              {...control}
              value={reason}
              maxLength={2000}
              onChange={(event) => onReasonChange(event.target.value)}
              onBlur={onReasonBlur}
            />
          )}
        </Field>
      </section>

      <section className='mt-8 flex flex-col gap-4'>
        <h2 id={programmeId} className='text-base font-semibold'>
          2. {m.closing_survey_programme_question({ programme })}
        </h2>
        <ScoreScale
          aria-labelledby={programmeId}
          min={1}
          max={5}
          value={answers.programme_application_score}
          onValueChange={(score) => onAnswer({ programme_application_score: score })}
          lowLabel={m.closing_survey_programme_low()}
          highLabel={m.closing_survey_programme_high()}
        />
      </section>

      <section className='mt-8 flex flex-col gap-4'>
        <h2 id={postponedId} className='text-base font-semibold'>
          3. {m.closing_survey_postponed_question()}
        </h2>
        <RadioOptionGroup<PostponedConversationAnswer | null>
          aria-labelledby={postponedId}
          value={answers.postponed_conversation}
          onValueChange={(choice) => {
            if (choice) onAnswer({ postponed_conversation: choice });
          }}
        >
          <RadioOption value='yes' label={m.closing_survey_postponed_yes()} />
          <RadioOption value='no' label={m.closing_survey_postponed_no()} />
          <RadioOption
            value='no_such_conversation'
            label={m.closing_survey_postponed_none()}
          />
        </RadioOptionGroup>
      </section>
    </>
  );
}
